import React, { useMemo, Suspense } from 'react';
import { Spin } from 'antd';
import { useAndStore } from '@nuwatop/code-canvas-core';
import { getComponent } from './ComponentRegistry';

const AntSpin = Spin as React.ComponentType<any>;

export interface DynamicContainerProps {
  /** 要渲染的组件名称 (如 "AndButton", "AndTable", "and-select" 等) */
  componentName: string;
  /** 静态 props (直接传递给子组件) */
  componentProps?: Record<string, any>;
  /** 从 Store 获取 props 的 key (格式: dataSetName 或 dataSetName.field) */
  storePropsKey?: string;
  /** 会话 ID */
  sessionId?: string;
  /** 自定义加载组件 */
  loadingFallback?: React.ReactNode;
  /** 组件未找到时的占位内容 */
  notFoundFallback?: React.ReactNode;
  /** 错误回调 */
  onError?: (error: Error) => void;
}

/**
 * DynamicContainer - 动态容器组件
 * 
 * 根据 componentName 动态渲染已注册的组件。
 * 采用"销毁重建 + Store"模式：
 * - 切换 componentName 时，旧组件销毁，新组件创建
 * - 组件状态通过 Store 保持，不依赖组件实例
 * 
 * @example
 * ```tsx
 * // 基本用法 - 静态 props
 * <DynamicContainer 
 *   componentName="AndButton" 
 *   componentProps={{ type: "primary", children: "Click me" }} 
 * />
 * 
 * // 从 Store 获取 props
 * <DynamicContainer 
 *   componentName="AndTable"
 *   storePropsKey="currentTableConfig"
 *   sessionId={sessionId}
 * />
 * 
 * // 混合使用
 * <DynamicContainer 
 *   componentName="AndSelect"
 *   componentProps={{ placeholder: "请选择" }}
 *   storePropsKey="selectOptions"
 *   sessionId={sessionId}
 * />
 * ```
 */
export const DynamicContainer: React.FC<DynamicContainerProps> = ({
  componentName,
  componentProps = {},
  storePropsKey,
  sessionId,
  loadingFallback,
  notFoundFallback,
  onError,
}) => {
  // 只订阅当前容器用到的 dataSet 切片，避免任意 setDataSet 触发本容器重渲染（解决输入延迟）
  const dataSetSlice = useAndStore((state) => {
    if (!storePropsKey) return null;
    const parts = storePropsKey.split('.');
    const dataSetName = parts[0];
    return state.dataSets[dataSetName];
  });

  const storeProps = useMemo(() => {
    if (!storePropsKey || !dataSetSlice) return {};
    const parts = storePropsKey.split('.');
    const field = parts[1];
    if (field) {
      return dataSetSlice[field] || {};
    }
    return dataSetSlice;
  }, [storePropsKey, dataSetSlice]);

  // 合并 props: storeProps 优先级低于 componentProps
  const mergedProps = useMemo(() => ({
    ...storeProps,
    ...componentProps,
    sessionId,
  }), [storeProps, componentProps, sessionId]);

  // 获取组件
  const Component = useMemo(() => {
    try {
      return getComponent(componentName);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      onError?.(error);
      return null;
    }
  }, [componentName, onError]);

  // 默认加载状态
  const defaultLoading = (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
      <AntSpin />
    </div>
  );

  // 组件未找到
  if (!Component) {
    if (notFoundFallback) {
      return <>{notFoundFallback}</>;
    }
    return (
      <div style={{ 
        padding: 16, 
        background: '#fff7e6', 
        border: '1px solid #ffd591', 
        borderRadius: 4,
        textAlign: 'center',
        color: '#d46b08' 
      }}>
        组件未找到: {componentName}
      </div>
    );
  }

  return (
    <Suspense fallback={loadingFallback || defaultLoading}>
      <Component {...mergedProps} />
    </Suspense>
  );
};

export default DynamicContainer;
